import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Group,
    Loader,
    Modal,
    Text,
    TextInput,
    Tooltip,
    CopyButton,
    Stack,
    Alert,
    UnstyledButton,
} from "@mantine/core";
import {DatePickerInput} from "@mantine/dates";
import {
    IconTrash,
    IconRefresh,
    IconPlus,
    IconCopy,
    IconCheck,
    IconAlertCircle,
    IconDownload
} from "@tabler/icons-react";
import {useNeurotuneStore} from "@/common/store/neurotune.ts";
import * as apiTokenService from "../../../common/services/apiTokenService";
import {IApiToken, IApiTokenCreateResponse} from "@/common/types";
import {showErrorNotification, showSuccessNotification} from "@/common/notifications.tsx";
import {modals} from "@mantine/modals";
import classes from "./ApiKeysPanel.module.css";
import commonClasses from "../../../common/common.module.css";
import cx from "clsx";
import dayjs from "dayjs";

export interface ApiKeysPanelProps {
}

export const ApiKeysPanel: React.FC<ApiKeysPanelProps> = () => {
    const activeCompany = useNeurotuneStore((state) => state.activeCompany);
    const [tokens, setTokens] = useState<IApiToken[]>([]);
    const [loading, setLoading] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [renewModalOpen, setRenewModalOpen] = useState(false);
    const [tokenDisplayModalOpen, setTokenDisplayModalOpen] = useState(false);
    const [selectedToken, setSelectedToken] = useState<IApiToken | null>(null);
    const [newTokenData, setNewTokenData] = useState<IApiTokenCreateResponse | null>(null);
    const [tokenSaved, setTokenSaved] = useState(false);

    // Create form state
    const [createName, setCreateName] = useState("");
    const [createExpirationDate, setCreateExpirationDate] = useState<Date>(
        dayjs().add(90, "days").toDate()
    );

    // Renew form state
    const [renewExpirationDate, setRenewExpirationDate] = useState<Date>(
        dayjs().add(90, "days").toDate()
    );

    useEffect(() => {
        loadTokens();
    }, [activeCompany]);

    const loadTokens = async () => {
        if (!activeCompany) return;

        setLoading(true);
        try {
            const loadedTokens = await apiTokenService.listApiTokens(activeCompany.id);
            setTokens(loadedTokens);
        } catch (error) {
            showErrorNotification({
                title: "Failed to load API tokens",
                message: "Could not retrieve your API tokens. Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateToken = async () => {
        if (!activeCompany || !createName.trim()) {
            showErrorNotification({
                title: "Validation Error",
                message: "Please provide a name for the API token.",
            });
            return;
        }

        const expiresInDays = dayjs(createExpirationDate).diff(dayjs(), "days");

        setLoading(true);
        try {
            const response = await apiTokenService.createApiToken(activeCompany.id, {
                name: createName,
                expires_in_days: expiresInDays,
            });

            setNewTokenData(response);
            setTokenSaved(false);
            setCreateModalOpen(false);
            setTokenDisplayModalOpen(true);

            // Reload tokens list
            await loadTokens();

            // Reset form
            setCreateName("");
            setCreateExpirationDate(dayjs().add(90, "days").toDate());
        } catch (error: any) {
            showErrorNotification({
                title: "Failed to create API token",
                message: error.response?.data?.message || "Could not create the API token. Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRenewToken = async () => {
        if (!activeCompany || !selectedToken) return;

        const expiresInDays = dayjs(renewExpirationDate).diff(dayjs(), "days");

        setLoading(true);
        try {
            await apiTokenService.renewApiToken(activeCompany.id, selectedToken.id, {
                expires_in_days: expiresInDays,
            });

            showSuccessNotification({
                title: "Token Renewed",
                message: `API token "${selectedToken.name}" has been renewed successfully.`,
            });

            setRenewModalOpen(false);
            await loadTokens();

            // Reset form
            setRenewExpirationDate(dayjs().add(90, "days").toDate());
        } catch (error: any) {
            showErrorNotification({
                title: "Failed to renew API token",
                message: error.response?.data?.message || "Could not renew the API token. Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteToken = (token: IApiToken) => {
        modals.openConfirmModal({
            title: "Delete API Token",
            children: (
                <Text>
                    Are you sure you want to delete the API token <Text span fw={700}>"{token.name}"</Text>?
                    This action cannot be undone and will immediately revoke access for this token.
                </Text>
            ),
            labels: {confirm: "Delete", cancel: "Cancel"},
            confirmProps: {color: "red"},
            onConfirm: async () => {
                if (!activeCompany) return;

                setLoading(true);
                try {
                    await apiTokenService.deleteApiToken(activeCompany.id, token.id);
                    showSuccessNotification({
                        title: "Token Deleted",
                        message: `API token "${token.name}" has been deleted.`,
                    });
                    await loadTokens();
                } catch (error: any) {
                    showErrorNotification({
                        title: "Failed to delete API token",
                        message: error.response?.data?.message || "Could not delete the API token. Please try again.",
                    });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const openRenewModal = (token: IApiToken) => {
        setSelectedToken(token);
        setRenewExpirationDate(dayjs().add(90, "days").toDate());
        setRenewModalOpen(true);
    };

    const handleCreateExpirationDateChange = (date: Date | null) => {
        if (!date) return;
        setCreateExpirationDate(date);
    };

    const handleRenewExpirationDateChange = (date: Date | null) => {
        if (!date) return;
        setRenewExpirationDate(date);
    };

    const downloadToken = (token: string, filename: string) => {
        const blob = new Blob([token], {type: "text/plain"});
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleTokenDisplayClose = () => {
        if (!tokenSaved) {
            modals.openConfirmModal({
                title: "Warning: Token Not Saved",
                children: (
                    <Text>
                        You haven't confirmed that you've saved this token. This is the only time you'll be able to view
                        it.
                        Are you sure you want to close this dialog?
                    </Text>
                ),
                labels: {confirm: "Yes, Close", cancel: "Go Back"},
                confirmProps: {color: "red"},
                onConfirm: () => {
                    setTokenDisplayModalOpen(false);
                    setNewTokenData(null);
                    setTokenSaved(false);
                },
            });
        } else {
            setTokenDisplayModalOpen(false);
            setNewTokenData(null);
            setTokenSaved(false);
        }
    };

    const formatDate = (date: Date) => {
        return dayjs(date).format("MMM D, YYYY h:mm A");
    };

    const isExpired = (date: Date) => {
        return dayjs(date).isBefore(dayjs());
    };

    const renderRows = () => tokens.map((token) => {
        const expired = isExpired(token.expiresAt);

        return (
            <Box key={token.id} className={cx(commonClasses.hover, classes.row)}>
                <Box className={classes.rowActions}>
                    <Tooltip label="Renew Token">
                        <UnstyledButton
                            onClick={() => openRenewModal(token)}
                            disabled={loading}
                        >
                            <IconRefresh className={cx(classes.iconRenew, classes.icon)}/>
                        </UnstyledButton>
                    </Tooltip>
                    <Tooltip label="Delete Token">
                        <UnstyledButton
                            onClick={() => handleDeleteToken(token)}
                            disabled={loading}
                        >
                            <IconTrash className={cx(classes.iconDelete, classes.icon)}/>
                        </UnstyledButton>
                    </Tooltip>
                </Box>
                <Box>
                    <Text size="sm">{token.name}</Text>
                </Box>
                <Box>
                    <Text size="sm" c={expired ? "red" : undefined}>
                        {formatDate(token.expiresAt)}
                        {expired && " (Expired)"}
                    </Text>
                </Box>
                <Box>
                    <Text size="sm">{formatDate(token.createdAt)}</Text>
                </Box>
                <Box>
                    <Text size="sm">
                        {token.lastUsedAt ? formatDate(token.lastUsedAt) : "Never"}
                    </Text>
                </Box>
            </Box>
        );
    });

    return (
        <Box className={classes.root}>
            <Box style={{overflowY: "auto"}}>
                <Box className={cx(classes.table)}>
                    <Box className={cx(classes.header, commonClasses.blur)}>
                        <Box className={cx(classes.headerRow)}>
                            <Box className={classes.rowActions}></Box>
                            <Box><Text fw={500} fz="sm">Name</Text></Box>
                            <Box><Text fw={500} fz="sm">Expires</Text></Box>
                            <Box><Text fw={500} fz="sm">Created</Text></Box>
                            <Box><Text fw={500} fz="sm">Last Used</Text></Box>
                            <Box style={{justifySelf:"end"}}>
                                <Button size={"compact-sm"}
                                        data-testid="create-token-button"
                                        onClick={() => setCreateModalOpen(true)}
                                        disabled={loading}>
                                            <IconPlus size={16}/>
                                </Button>
                            </Box>

                        </Box>
                    </Box>
                    <Box>
                        {loading && tokens.length === 0 ? (
                            <Box className={classes.loaderContainer}>
                                <Loader data-testid="loading"/>
                            </Box>
                        ) : tokens.length === 0 ? (
                            <Box className={classes.emptyState}>
                                <Text size="lg" c="dimmed">No API keys found</Text>
                                <Text size="sm" c="dimmed">Create your first API key to get started</Text>
                            </Box>
                        ) : (
                            renderRows()
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Create Token Modal */}
            <Modal
                data-testid="create-token-modal"
                opened={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                title="Create New API Token"
                size="md"
            >
                <Stack>
                    <TextInput
                        label="Token Name"
                        placeholder="My API Token"
                        value={createName}
                        onChange={(e) => setCreateName(e.currentTarget.value)}
                        required
                        description="A descriptive name for this token"
                    />

                    <DatePickerInput
                        label="Expiration Date"
                        value={createExpirationDate}
                        onChange={handleCreateExpirationDateChange}
                        minDate={dayjs().add(7, "days").toDate()}
                        maxDate={dayjs().add(365, "days").toDate()}
                        required
                        description="Token will expire on this date (7 days to 1 year from now)"
                    />

                    <Group justify="flex-end" mt="md">
                        <Button variant="default" onClick={() => setCreateModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreateToken} loading={loading}>
                            Create Token
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Renew Token Modal */}
            <Modal
                opened={renewModalOpen}
                onClose={() => setRenewModalOpen(false)}
                title={`Renew API Token: ${selectedToken?.name}`}
                size="md"
            >
                <Stack>
                    <DatePickerInput
                        label="New Expiration Date"
                        value={renewExpirationDate}
                        onChange={handleRenewExpirationDateChange}
                        minDate={dayjs().add(7, "days").toDate()}
                        maxDate={dayjs().add(365, "days").toDate()}
                        required
                        description="Token will expire on this date (7 days to 1 year from now)"
                    />

                    <Group justify="flex-end" mt="md">
                        <Button variant="default" onClick={() => setRenewModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleRenewToken} loading={loading}>
                            Renew Token
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Token Display Modal */}
            <Modal
                opened={tokenDisplayModalOpen}
                onClose={handleTokenDisplayClose}
                title="API Token Created"
                size="lg"
                closeOnClickOutside={false}
                closeOnEscape={false}
                withCloseButton={tokenSaved}
            >
                <Stack>
                    <Alert icon={<IconAlertCircle size={16}/>} color="red" title="Important: Save Your Token">
                        This is the only time you will be able to view this token. Make sure to copy it now and store it
                        securely.
                        If you lose this token, you will need to create a new one.
                    </Alert>

                    {newTokenData && (
                        <>
                            <Box>
                                <Text size="sm" fw={500} mb="xs">Token Name</Text>
                                <Text size="sm" c="dimmed">{newTokenData.name}</Text>
                            </Box>

                            <Box>
                                <Text size="sm" fw={500} mb="xs">API Token</Text>
                                <Box className={classes.tokenDisplay}>
                                    <Text size="xs" className={classes.tokenText}>
                                        {newTokenData.token}
                                    </Text>
                                </Box>
                            </Box>

                            <Group justify="space-between">
                                <Group>
                                    <CopyButton value={newTokenData.token}>
                                        {({copied, copy}) => (
                                            <Button
                                                leftSection={copied ? <IconCheck size={16}/> : <IconCopy size={16}/>}
                                                color={copied ? "teal" : "blue"}
                                                onClick={copy}
                                            >
                                                {copied ? "Copied!" : "Copy Token"}
                                            </Button>
                                        )}
                                    </CopyButton>

                                    <Button
                                        leftSection={<IconDownload size={16}/>}
                                        variant="default"
                                        onClick={() => downloadToken(
                                            newTokenData.token,
                                            `${newTokenData.name.replace(/\s+/g, "_")}_token.txt`
                                        )}
                                    >
                                        Download Token
                                    </Button>
                                </Group>

                                <Button
                                    color="green"
                                    onClick={() => {
                                        setTokenSaved(true);
                                        setTokenDisplayModalOpen(false);
                                        setNewTokenData(null);
                                    }}
                                >
                                    I've Saved This Token
                                </Button>
                            </Group>
                        </>
                    )}
                </Stack>
            </Modal>
        </Box>
    );
};

